import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { Headset, Send } from "lucide-react";
import { useNotifications } from "../context/NotificationContext";

const ContactAdmin = () => {
  const { items } = useNotifications();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/merchant/admin-thread");
      setMessages(r.data);
    } catch (e) { toast.error(arabicError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (items[0]?.type === "admin_message") load();
  }, [items, load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim()) return;
    setSending(true);
    try {
      const r = await api.post("/merchant/contact-admin", { message: input.trim() });
      setMessages((prev) => [...prev, r.data]);
      setInput("");
      toast.success("تم إرسال رسالتك إلى الإدارة");
    } catch (e) { toast.error(arabicError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="contact-admin-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333] flex items-center gap-3"><Headset className="text-[#09637E]" /> تواصل مع الإدارة</h1>
        <p className="text-[#666666] mt-1">أرسل استفسارك أو ملاحظتك لإدارة إمداد وسيصلك الرد هنا وفي الإشعارات</p>
      </div>
      <div className="bg-white border border-[#7AB2B2] rounded-2xl flex flex-col h-[70vh]">
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="contact-admin-messages">
          {loading ? (
            <div className="text-center text-[#666666] py-8">جارٍ التحميل...</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-[#666666] py-10" data-testid="contact-admin-empty">لا توجد رسائل بعد — ابدأ المحادثة مع الإدارة</div>
          ) : messages.map((m) => {
            const mine = m.direction === "to_admin";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-start" : "justify-end"}`} data-testid={`contact-admin-msg-${m.id}`}>
                <div className={`max-w-[80%] rounded-2xl p-3 ${mine ? "bg-[#09637E] text-white" : "bg-[#EBF4F6] text-[#333333]"}`}>
                  <div className="text-xs font-bold opacity-80 mb-1">{mine ? "أنت" : "إدارة إمداد"}</div>
                  <div className="whitespace-pre-wrap break-words">{m.message}</div>
                  <div className="text-[10px] opacity-70 mt-1">{new Date(m.ts).toLocaleString("ar-SY", { dateStyle: "short", timeStyle: "short" })}</div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="border-t border-[#7AB2B2] p-3 flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="contact-admin-input"
            placeholder="اكتب رسالتك للإدارة..." maxLength={2000}
            className="h-12 flex-1 min-w-0 rounded-lg border border-[#7AB2B2] px-4 focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
          <button type="submit" disabled={sending || !input.trim()} data-testid="contact-admin-send"
            className="h-12 px-5 shrink-0 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold flex items-center gap-2 disabled:opacity-50">
            <Send size={18} /> إرسال
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactAdmin;
